/**
 * Batches — a class / cohort of learners with the courses they follow.
 * Instructor: /instructor/batches (own batches). Tenant admin: /admin/batches (all batches).
 * New / Edit are small forms → side pane; Open goes to the batch tab (BatchDetail).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Page, StatTiles } from '../../components/shell/Page';
import SplitLayout, { paneForm } from '../../components/shell/SidePane';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, UsersThree, Eye, Books, Student, CalendarBlank } from '@phosphor-icons/react';

export const BATCH_STATUSES = [['active', 'Active'], ['completed', 'Completed'], ['archived', 'Archived']];
const EMPTY = { name: '', code: '', description: '', instructor_id: '', start_date: '', end_date: '', status: 'active' };

export function batchBasePath(role) {
  return role === 'tenant_admin' ? '/admin/batches' : '/instructor/batches';
}

export function BatchFormFields({ form, setForm, instructors, isAdmin }) {
  return (
    <>
      <div className="pane-grid">
        <div className="field">
          <Label>Batch name <span className="text-destructive">*</span></Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Class 10 — Section A" data-testid="batch-name-input" />
        </div>
        <div className="field">
          <Label>Code</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. C10A-2026" className="font-mono uppercase" data-testid="batch-code-input" />
        </div>
        <div className="field">
          <Label>Start date</Label>
          <Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
        </div>
        <div className="field">
          <Label>End date</Label>
          <Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>
        {isAdmin && (
          <div className="field">
            <Label>Instructor</Label>
            <select className="form-select" value={form.instructor_id || ''} onChange={(e) => setForm({ ...form, instructor_id: e.target.value })} data-testid="batch-instructor-select">
              <option value="">— Not assigned —</option>
              {instructors.map((i) => <option key={i.id} value={i.id}>{i.first_name} {i.last_name}</option>)}
            </select>
          </div>
        )}
        <div className="field">
          <Label>Status</Label>
          <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {BATCH_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="field">
        <Label>Description</Label>
        <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Who is this batch for, schedule, notes…" />
      </div>
    </>
  );
}

export default function Batches() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'tenant_admin';
  const base = batchBasePath(user?.role);
  const [batches, setBatches] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paneMode, setPaneMode] = useState(null); // 'new' | 'edit'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/batches');
      setBatches(res.data || []);
      if (isAdmin) {
        try { const ins = await api.get('/instructors'); setInstructors(ins.data || []); } catch { setInstructors([]); }
      }
    } catch (error) {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  }, [api, isAdmin]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const closePane = () => { setPaneMode(null); setEditing(null); setForm({ ...EMPTY }); };
  const openNew = () => { setEditing(null); setForm({ ...EMPTY }); setPaneMode('new'); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name || '', code: b.code || '', description: b.description || '', instructor_id: b.instructor_id || '', start_date: b.start_date || '', end_date: b.end_date || '', status: b.status || 'active' }); setPaneMode('edit'); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, code: form.code || null, instructor_id: form.instructor_id || null, start_date: form.start_date || null, end_date: form.end_date || null };
      if (paneMode === 'edit' && editing) {
        await api.put(`/batches/${editing.id}`, payload);
        toast.success('Batch updated');
        closePane();
        fetchData();
      } else {
        const res = await api.post('/batches', payload);
        toast.success('Batch created — now add students and courses');
        closePane();
        navigate(`${base}/${res.data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save batch');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete batch "${b.name}"? Students keep their course enrolments.`)) return;
    try {
      await api.delete(`/batches/${b.id}`);
      toast.success('Batch deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete batch');
    }
  };

  let side = null;
  if (paneMode) {
    const f = paneForm({ id: 'batch-form', onSubmit: save, onCancel: closePane, submitLabel: paneMode === 'edit' ? 'Save changes' : 'Create batch', submitting: saving, submitIcon: paneMode === 'edit' ? PencilSimple : Plus,
      children: <BatchFormFields form={form} setForm={setForm} instructors={instructors} isAdmin={isAdmin} /> });
    side = { open: true, onOpenChange: closePane, title: paneMode === 'edit' ? `Edit: ${editing?.name}` : 'New batch', subtitle: paneMode === 'edit' ? 'Update batch details' : 'A class or cohort of learners', icon: UsersThree, children: f.body, footer: f.footer, testid: 'batch-pane' };
  }

  const totalStudents = batches.reduce((n, b) => n + (b.student_count || 0), 0);

  return (
    <DashboardLayout>
      <SplitLayout side={side}>
        <Page data-testid="batches-page">
          <StatTiles items={[
            { label: 'Batches', value: batches.length, icon: UsersThree },
            { label: 'Active', value: batches.filter((b) => b.status === 'active').length, icon: CalendarBlank, iconClass: 'bg-emerald-100 text-emerald-700' },
            { label: 'Students (all batches)', value: totalStudents, icon: Student, iconClass: 'bg-sky-100 text-sky-700' },
            { label: 'Courses assigned', value: batches.reduce((n, b) => n + (b.course_count || 0), 0), icon: Books, iconClass: 'bg-violet-100 text-violet-700' },
          ]} />
          <DataGrid
            title="Batches"
            rows={batches}
            loading={loading}
            onRefresh={fetchData}
            searchKeys={['name', 'code', 'instructor_name', 'description']}
            columns={[
              { key: 'name', header: 'Batch', render: (b) => <span className="font-medium">{b.name}</span> },
              { key: 'code', header: 'Code', width: 120, className: 'font-mono text-xs', accessor: (b) => b.code || '' , render: (b) => b.code || '—' },
              { key: 'instructor_name', header: 'Instructor', width: 160, accessor: (b) => b.instructor_name || '', render: (b) => b.instructor_name || '—' },
              { key: 'student_count', header: 'Students', width: 100, align: 'right' },
              { key: 'course_count', header: 'Courses', width: 100, align: 'right', render: (b) => <span title={(b.courses || []).map((c) => c.title).join(', ')}>{b.course_count}</span> },
              { key: 'start_date', header: 'Starts', width: 110, render: (b) => b.start_date ? new Date(b.start_date).toLocaleDateString() : '—' },
              { key: 'end_date', header: 'Ends', width: 110, render: (b) => b.end_date ? new Date(b.end_date).toLocaleDateString() : '—' },
              { key: 'status', header: 'Status', width: 110, render: (b) => <StatusBadge value={b.status} /> },
            ]}
            rowTone={(b) => (b.status === 'archived' ? 'muted' : b.status === 'completed' ? 'success' : null)}
            onRowDoubleClick={(b) => navigate(`${base}/${b.id}`)}
            primaryActions={(
              <Button size="sm" className="h-9" onClick={openNew} data-testid="new-batch-btn"><Plus className="w-4 h-4" /> New batch</Button>
            )}
            selectionActions={[
              { key: 'open', label: 'Open', icon: Eye, onClick: ([b]) => navigate(`${base}/${b.id}`) },
              { key: 'edit', label: 'Edit', icon: PencilSimple, onClick: ([b]) => openEdit(b) },
              { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: ([b]) => remove(b) },
            ]}
            emptyMessage="No batches yet — create one, then add students and assign courses"
          />
        </Page>
      </SplitLayout>
    </DashboardLayout>
  );
}
